/**
 * VAST Parser - Parser per tag VAST Google Ad Manager
 * 
 * Supporta VAST 2.0, 3.0, 4.0 con focus su creatività audio.
 * Estrae il MediaFile audio dal tag VAST per riproduzione.
 * 
 * @since v3.0.0 - Nuovo sistema ads con supporto GAM/VAST
 */

export class VASTParser {
  /**
   * Costruttore
   */
  constructor() {
    this.vastDocument = null;
  }
  
  /**
   * Fetch e parse tag VAST
   * 
   * @param {string} vastTagUrl - URL del tag VAST
   * @returns {Promise<Object>} Oggetto con dati ad estratti
   * @throws {Error} Se fetch o parsing fallisce
   */
  async parse(vastTagUrl) {
    try {
      console.log('📡 Fetching VAST tag:', vastTagUrl);
      
      const response = await fetch(vastTagUrl);
      if (!response.ok) {
        throw new Error(`VAST fetch failed: ${response.status}`);
      }
      
      const vastXml = await response.text();
      const parser = new DOMParser();
      this.vastDocument = parser.parseFromString(vastXml, 'text/xml');
      
      // Verifica errori parsing
      const parserError = this.vastDocument.querySelector('parsererror');
      if (parserError) {
        throw new Error('Invalid VAST XML');
      }
      
      // Estrai dati
      const adData = this.extractAdData();
      
      console.log('✅ VAST parsed:', adData);
      return adData;
      
    } catch (error) {
      console.error('❌ VAST parsing error:', error);
      throw error;
    }
  }
  
  /**
   * Estrai dati ad dal documento VAST
   * 
   * @returns {Object} Dati ad estratti
   * @private
   */
  extractAdData() {
    // Trova primo Linear ad
    const linear = this.vastDocument.querySelector('Linear');
    if (!linear) {
      throw new Error('No Linear ad found in VAST');
    }
    
    // Duration
    const durationEl = linear.querySelector('Duration');
    const duration = durationEl ? this.parseDuration(durationEl.textContent) : 0;
    
    // MediaFiles
    const mediaFiles = this.extractMediaFiles(linear);
    if (!mediaFiles.length) {
      throw new Error('No audio MediaFile found in VAST');
    }
    
    // Tracking events
    const trackingEvents = this.extractTrackingEvents(linear);
    
    // Click tracking
    const clickThrough = this.vastDocument.querySelector('ClickThrough')?.textContent?.trim();
    const clickTracking = this.vastDocument.querySelector('ClickTracking')?.textContent?.trim();
    
    return {
      duration,
      mediaFile: mediaFiles[0], // Prendi il primo audio disponibile
      allMediaFiles: mediaFiles,
      trackingEvents,
      clickThrough,
      clickTracking
    };
  }
  
  /**
   * Estrai MediaFiles audio dal Linear
   * 
   * @param {Element} linear - Elemento Linear del VAST
   * @returns {Array<Object>} Array di MediaFiles audio
   * @private
   */
  extractMediaFiles(linear) {
    const mediaFileElements = linear.querySelectorAll('MediaFile');
    const audioFiles = [];
    
    mediaFileElements.forEach(mf => {
      const type = mf.getAttribute('type');
      const url = mf.textContent.trim();
      
      // Filtra solo audio MIME types
      if (type && type.startsWith('audio/')) {
        audioFiles.push({
          url,
          type,
          bitrate: parseInt(mf.getAttribute('bitrate')) || 0,
          width: parseInt(mf.getAttribute('width')) || 0,
          height: parseInt(mf.getAttribute('height')) || 0
        });
      }
    });
    
    // Ordina per bitrate (migliore qualità prima)
    return audioFiles.sort((a, b) => b.bitrate - a.bitrate);
  }
  
  /**
   * Estrai tracking events
   * 
   * @param {Element} linear - Elemento Linear del VAST
   * @returns {Object} Mappa event → URLs
   * @private
   */
  extractTrackingEvents(linear) {
    const trackingElements = linear.querySelectorAll('Tracking');
    const events = {};
    
    trackingElements.forEach(tracking => {
      const event = tracking.getAttribute('event');
      const url = tracking.textContent.trim();
      
      if (event && url) {
        if (!events[event]) {
          events[event] = [];
        }
        events[event].push(url);
      }
    });
    
    return events;
  }
  
  /**
   * Parse duration VAST (formato HH:MM:SS)
   * 
   * @param {string} durationString - Stringa duration
   * @returns {number} Durata in secondi
   * @private
   */
  parseDuration(durationString) {
    const parts = durationString.split(':');
    if (parts.length !== 3) return 0;
    
    const hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]) || 0;
    const seconds = parseFloat(parts[2]) || 0;
    
    return (hours * 3600) + (minutes * 60) + seconds;
  }
  
  /**
   * Fire tracking pixel
   * 
   * @param {string} url - URL del tracking pixel
   * @static
   */
  static fireTracking(url) {
    if (!url) return;
    
    // Usa Image per firing pixel (non blocca, cross-origin safe)
    const img = new Image();
    img.src = url;
    
    console.log('📊 VAST tracking fired:', url);
  }
  
  /**
   * Fire multiple tracking URLs
   * 
   * @param {Array<string>} urls - Array di URLs
   * @static
   */
  static fireTrackingArray(urls) {
    if (!urls || !Array.isArray(urls)) return;
    urls.forEach(url => VASTParser.fireTracking(url));
  }
}

